import { Typography } from '@mui/material';
import { isHtmlDescription, sanitizeRichText } from '../../utils/richText';

interface GatheringDescriptionProps {
  description: string | null | undefined;
}

/**
 * Read-only render of a gathering description (ENG-1870).
 *
 * Descriptions written before hosts got the rich-text editor are plain text
 * whose line breaks exist only because of `white-space: pre-line`, so those keep
 * the original render untouched. Editor-authored HTML takes the sanitised
 * branch, with the block styles a bare `<div>` would otherwise lose.
 */
export default function GatheringDescription({ description }: GatheringDescriptionProps) {
  if (!description) {
    return <Typography variant="body1">No description provided.</Typography>;
  }

  if (!isHtmlDescription(description)) {
    return (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        {description}
      </Typography>
    );
  }

  return (
    <Typography
      variant="body1"
      component="div"
      sx={richTextSx}
      dangerouslySetInnerHTML={{ __html: sanitizeRichText(description) }}
    />
  );
}

/** Mirrors web's RICH_TEXT_DESCRIPTION_CLASS block styles. */
export const richTextSx = {
  '& p': { my: 1.5 },
  '& > p:first-of-type': { mt: 0 },
  '& > p:last-child': { mb: 0 },
  '& strong, & b': { fontWeight: 600 },
  '& ul, & ol': { my: 1.5, pl: 3 },
  '& li': { my: 0.5 },
  '& h2': { fontSize: '1.125rem', fontWeight: 600, mt: 2.5, mb: 1 },
  '& h3': { fontSize: '1rem', fontWeight: 600, mt: 2, mb: 1 },
  '& a': { color: 'primary.main', textDecoration: 'underline' },
  '& blockquote': {
    my: 1.5,
    mx: 0,
    pl: 2,
    borderLeft: 4,
    borderColor: 'divider',
    fontStyle: 'italic',
  },
  '& img': { my: 1.5, maxWidth: '100%', height: 'auto', borderRadius: 2 },
  '& hr': { my: 2, border: 0, borderTop: 1, borderColor: 'divider' },
} as const;
